// Banque, administration, santé et arnaques.
import type { EventDef } from '../events';
import type { GameState, StageId } from '../../engine/state';
import { overdraftLimit } from '../../game/actions';
import { canOpenAv } from '../../game/av';
import { careCost } from '../../game/health';
import { hasSubscription } from '../../game/subscriptions';
import { age, between, roll, simpleEvent, working } from './kit';

const ALL_STAGES: StageId[] = ['prologue', 'stage1', 'stage2'];
const ADULT: StageId[] = ['stage1', 'stage2'];

/** Jours de jeu depuis le premier emploi, ou −1 avant. */
function daysWorking(state: GameState): number {
  return state.firstJobDay === null ? -1 : state.day - state.firstJobDay;
}

/** Jours de jeu depuis la dernière venue d'un événement (l'infini s'il n'est jamais venu). */
function daysSince(state: GameState, id: string): number {
  const last = state.eventLastDays[id];
  return last === undefined ? Infinity : state.day - last;
}

/** Un montant au hasard, plus petit pendant les études (pour params). */
function byStage(state: GameState, student: [number, number], adult: [number, number]): number {
  const [min, max] = state.stage === 'prologue' ? student : adult;
  return between(state, min, max);
}

export const EVENTS: EventDef[] = [
  // La banque et la carte
  simpleEvent({
    id: 'banque_en_ligne',
    kind: 'quotidien',
    stages: ALL_STAGES,
    condition: (state) => (state.expenses.banque?.amount ?? 0) > 0,
    params: (state) => {
      const fees = state.expenses.banque?.amount ?? 8;
      return { fees, saving: Math.max(1, Math.round(fees / 2)) };
    },
    choices: [
      {
        id: 'changer',
        effects: {
          setExpense: { id: 'banque', amount: 0 },
          energy: -3,
          flag: 'banque_en_ligne_ouverte',
          followUp: { event: 'prime_bienvenue_banque', days: 30 },
        },
      },
      { id: 'negocier', effects: (_, p) => ({ addExpense: { id: 'banque', amount: -p.saving }, energy: -1 }) },
      { id: 'garder' },
    ],
  }),
  simpleEvent({
    // Un mois après l'ouverture d'un compte en ligne.
    id: 'prime_bienvenue_banque',
    kind: 'chance',
    stages: ALL_STAGES,
    manual: true,
    params: (state) => ({ gain: between(state, 80, 150) }),
    choices: [
      { id: 'livret', effects: (_, p) => ({ livret: p.gain, wellbeing: 1 }) },
      { id: 'compte', effects: (_, p) => ({ cash: p.gain }) },
    ],
  }),
  simpleEvent({
    id: 'carte_bancaire_perdue',
    kind: 'quotidien',
    stages: ALL_STAGES,
    params: (state) => ({ card: between(state, 10, 20) }),
    choices: [
      { id: 'opposition', effects: (_, p) => ({ cash: -p.card }) },
      { id: 'chercher', effects: { energy: -2, wellbeing: -2 } },
    ],
  }),
  simpleEvent({
    id: 'carte_avalee',
    kind: 'quotidien',
    stages: ALL_STAGES,
    params: (state) => ({ card: between(state, 10, 20) }),
    choices: [
      { id: 'agence', effects: { energy: -5, wellbeing: -1 } },
      { id: 'nouvelle', effects: (_, p) => ({ cash: -p.card }) },
    ],
  }),
  simpleEvent({
    id: 'frais_hors_zone_euro',
    kind: 'quotidien',
    stages: ALL_STAGES,
    season: [6, 7, 8],
    cooldownDays: 300,
    condition: (state) => (state.expenses.banque?.amount ?? 0) > 0 && !state.flags.banque_en_ligne_ouverte,
    params: (state) => ({ fee: 3, many: between(state, 25, 45), once: between(state, 8, 12) }),
    choices: [
      { id: 'petits', effects: (_, p) => ({ cash: -p.many }) },
      { id: 'gros', effects: (_, p) => ({ cash: -p.once, wellbeing: -1 }) },
      {
        id: 'carte',
        effects: {
          energy: -3,
          flag: 'banque_en_ligne_ouverte',
          followUp: { event: 'prime_bienvenue_banque', days: 30 },
        },
      },
    ],
  }),
  simpleEvent({
    id: 'virement_par_erreur',
    kind: 'tentation',
    stages: ALL_STAGES,
    maxCount: 1,
    params: (state) => ({ amount: byStage(state, [200, 350], [350, 600]) }),
    choices: [
      { id: 'signaler', effects: { energy: -1, wellbeing: 1 } },
      { id: 'garder', effects: (_, p) => ({ cash: p.amount, followUp: { event: 'virement_repris', days: 60 } }) },
    ],
  }),
  simpleEvent({
    // Deux mois après le virement gardé : la banque le reprend, avec des frais.
    id: 'virement_repris',
    kind: 'imprevu',
    stages: ALL_STAGES,
    manual: true,
    bad: true,
    shock: 5,
    params: (state) => ({ fee: between(state, 20, 35) }),
    choices: [
      { id: 'compte', effects: (_, p) => ({ cash: -(p.amount + p.fee) }) },
      { id: 'livret', effects: (_, p) => ({ livret: -(p.amount + p.fee) }) },
    ],
  }),
  simpleEvent({
    id: 'prelevement_rejete',
    kind: 'imprevu',
    stages: ALL_STAGES,
    bad: true,
    shock: 5,
    // Au-delà du découvert autorisé, la banque rejette les prélèvements.
    condition: (state) => state.cash < -overdraftLimit(state),
    params: (state) => ({ fee: 20, penalty: between(state, 7, 15) }),
    choices: [
      { id: 'compte', effects: (_, p) => ({ cash: -(p.fee + p.penalty) }) },
      { id: 'livret', effects: (_, p) => ({ livret: -(p.fee + p.penalty) }) },
    ],
  }),
  simpleEvent({
    id: 'assurance_moyens_paiement',
    kind: 'quotidien',
    stages: ALL_STAGES,
    maxCount: 1,
    condition: (state) => (state.expenses.banque?.amount ?? 0) >= 2,
    params: (state) => ({ fee: between(state, 20, 30) }),
    choices: [
      {
        id: 'contester',
        effects: (_, p) => ({ cash: p.fee, addExpense: { id: 'banque', amount: -2 }, energy: -2 }),
      },
      { id: 'laisser' },
    ],
  }),

  // Les arnaques
  simpleEvent({
    id: 'sms_faux_colis',
    kind: 'imprevu',
    stages: ALL_STAGES,
    page: 'arnaques',
    params: (state) => ({ fee: between(state, 1, 3), loss: byStage(state, [90, 200], [150, 400]) }),
    choices: [
      { id: 'payer', effects: (_, p) => ({ cash: -p.loss, wellbeing: -3 }) },
      { id: 'verifier', effects: { energy: -1, knowledge: 1 } },
      { id: 'supprimer' },
    ],
  }),
  simpleEvent({
    id: 'faux_conseiller_bancaire',
    kind: 'imprevu',
    stages: ALL_STAGES,
    page: 'arnaques',
    params: (state) => ({
      suspect: byStage(state, [300, 600], [600, 1200]),
      loss: byStage(state, [250, 500], [600, 1200]),
    }),
    choices: [
      { id: 'valider', effects: (_, p) => ({ cash: -p.loss, wellbeing: -4 }) },
      { id: 'rappeler', effects: { energy: -1, knowledge: 1 } },
    ],
  }),
  simpleEvent({
    id: 'faux_support_technique',
    kind: 'imprevu',
    stages: ALL_STAGES,
    page: 'arnaques',
    params: (state) => ({ cost: byStage(state, [100, 200], [150, 300]) }),
    choices: [
      { id: 'appeler', effects: (_, p) => ({ cash: -p.cost, wellbeing: -2 }) },
      { id: 'eteindre', effects: { knowledge: 1 } },
    ],
  }),
  simpleEvent({
    id: 'cryptomonnaie_ami',
    kind: 'tentation',
    stages: ALL_STAGES,
    params: (state) => {
      const stake = byStage(state, [100, 200], [300, 800]);
      const small = state.stage === 'prologue' ? 20 : 50;
      // Ce que vaut la mise à la revente, six mois plus tard : de 15 % à 130 % de départ.
      const ratio = roll(state, 15, 130) / 100;
      return { stake, back: Math.round(stake * ratio), small, backSmall: Math.round(small * ratio) };
    },
    choices: [
      { id: 'miser', effects: (_, p) => ({ cash: p.back - p.stake, energy: -2 }) },
      { id: 'petit', effects: (_, p) => ({ cash: p.backSmall - p.small }) },
      { id: 'refuser' },
    ],
  }),
  simpleEvent({
    id: 'location_vacances_fantome',
    kind: 'tentation',
    stages: ALL_STAGES,
    season: [3, 4, 5],
    cooldownDays: 300,
    maxCount: 2,
    page: 'arnaques',
    params: (state) => ({ deposit: byStage(state, [150, 250], [300, 500]) }),
    choices: [
      { id: 'verser', effects: (_, p) => ({ cash: -p.deposit, wellbeing: -3 }) },
      { id: 'plateforme', effects: { wellbeing: -1, knowledge: 1 } },
    ],
  }),
  simpleEvent({
    id: 'fausse_amende_courriel',
    kind: 'imprevu',
    stages: ALL_STAGES,
    page: 'arnaques',
    params: (state) => ({ fine: 35, loss: byStage(state, [120, 250], [200, 450]) }),
    choices: [
      { id: 'payer', effects: (_, p) => ({ cash: -p.loss, wellbeing: -3 }) },
      { id: 'verifier', effects: { energy: -1, knowledge: 1 } },
    ],
  }),
  simpleEvent({
    id: 'faux_controle_detecteur',
    kind: 'quotidien',
    stages: ALL_STAGES,
    page: 'arnaques',
    condition: (state) => state.housing !== 'parents',
    params: (state) => ({ price: between(state, 150, 250), real: between(state, 15, 25) }),
    choices: [
      { id: 'payer', effects: (_, p) => ({ cash: -p.price }) },
      { id: 'refuser' },
      { id: 'verifier', effects: { energy: -1, knowledge: 1 } },
    ],
  }),
  simpleEvent({
    id: 'faux_proche_nouveau_numero',
    kind: 'proches',
    stages: ALL_STAGES,
    page: 'arnaques',
    params: (state) => ({ amount: byStage(state, [150, 300], [250, 450]) }),
    choices: [
      { id: 'payer', effects: (_, p) => ({ cash: -p.amount, wellbeing: -3 }) },
      { id: 'appeler', effects: { wellbeing: 1 } },
      { id: 'question', effects: { knowledge: 1 } },
    ],
  }),
  simpleEvent({
    id: 'faux_acheteur_revente',
    kind: 'quotidien',
    stages: ALL_STAGES,
    page: 'arnaques',
    params: (state) => ({ price: between(state, 60, 150), loss: byStage(state, [100, 250], [150, 400]) }),
    choices: [
      { id: 'lien', effects: (_, p) => ({ cash: -p.loss, wellbeing: -3 }) },
      { id: 'appli', effects: (_, p) => ({ cash: p.price, energy: -1 }) },
    ],
  }),
  simpleEvent({
    id: 'abonnement_cache',
    kind: 'tentation',
    stages: ALL_STAGES,
    page: 'arnaques',
    params: (state) => ({ fee: between(state, 1, 3), monthly: between(state, 30, 50) }),
    choices: [
      // Deux mois d'abonnement caché avant de réussir à l'arrêter.
      { id: 'payer', effects: (_, p) => ({ cash: -(p.fee + 2 * p.monthly), wellbeing: -2 }) },
      { id: 'fermer' },
    ],
  }),
  simpleEvent({
    id: 'faux_site_administratif',
    kind: 'quotidien',
    stages: ALL_STAGES,
    page: 'arnaques',
    params: (state) => ({ cost: between(state, 25, 40) }),
    choices: [
      { id: 'payer', effects: (_, p) => ({ cash: -p.cost }) },
      { id: 'officiel', effects: { energy: -1, knowledge: 1 } },
    ],
  }),
  simpleEvent({
    id: 'faux_rib_loyer',
    kind: 'imprevu',
    stages: ALL_STAGES,
    bad: true,
    shock: 8,
    maxCount: 1,
    page: 'arnaques',
    condition: (state) => state.housing === 'coloc' || state.housing === 'studio',
    params: (state) => ({ rent: Math.round(state.expenses.logement?.amount ?? 450) }),
    choices: [
      { id: 'livret', effects: (_, p) => ({ livret: -p.rent }) },
      { id: 'compte', effects: (_, p) => ({ cash: -p.rent }) },
      { id: 'credit', effects: (_, p) => ({ loan: { amount: p.rent, rate: 0.08, months: 6 } }) },
    ],
  }),

  // Les papiers et les impôts
  simpleEvent({
    id: 'premiere_declaration_impots',
    kind: 'etape',
    stages: ADULT,
    season: [4, 5, 6],
    maxCount: 1,
    condition: (state) => daysWorking(state) >= 90,
    params: (state) => ({ pizza: between(state, 15, 25) }),
    choices: [
      { id: 'remplir', effects: { energy: -3, xp: { skill: 'organisation', amount: 10 } } },
      { id: 'ami', effects: (_, p) => ({ cash: -p.pizza, energy: -1, wellbeing: 1 }) },
      { id: 'centre', effects: { energy: -4, wellbeing: 2 } },
    ],
  }),
  simpleEvent({
    id: 'remboursement_impots',
    kind: 'chance',
    pleasureCategory: 'voyages',
    stages: ADULT,
    season: [7, 8],
    cooldownDays: 300,
    condition: (state) => daysWorking(state) >= 240,
    params: (state) => ({ gain: between(state, 60, 300) }),
    choices: [
      { id: 'livret', effects: (_, p) => ({ livret: p.gain, wellbeing: 1 }) },
      { id: 'vacances', effects: { pleasure: 3 } },
    ],
  }),
  simpleEvent({
    id: 'regularisation_impots',
    kind: 'imprevu',
    stages: ADULT,
    bad: true,
    shock: 5,
    season: [9],
    cooldownDays: 300,
    // Pas de reste à payer l'année où le fisc a remboursé.
    condition: (state) => daysWorking(state) >= 240 && daysSince(state, 'remboursement_impots') > 90,
    params: (state) => {
      // Un taux laissé tel quel l'an dernier, alors que le salaire avait grimpé : la note est plus salée.
      const stale = state.flags.taux_prelevement_ancien === true && daysSince(state, 'taux_prelevement_source') < 450;
      return { due: stale ? between(state, 300, 600) : between(state, 100, 300) };
    },
    choices: [
      { id: 'compte', effects: (_, p) => ({ cash: -p.due }) },
      { id: 'livret', effects: (_, p) => ({ livret: -p.due }) },
    ],
  }),
  simpleEvent({
    id: 'taux_prelevement_source',
    kind: 'quotidien',
    stages: ADULT,
    maxCount: 1,
    condition: (state) => daysWorking(state) >= 300,
    params: (state) => ({ extra: between(state, 60, 150) }),
    choices: [
      { id: 'actualiser', effects: (_, p) => ({ cash: -p.extra, energy: -1 }) },
      { id: 'laisser', effects: { flag: 'taux_prelevement_ancien' } },
    ],
  }),
  simpleEvent({
    id: 'prime_activite',
    kind: 'chance',
    stages: ADULT,
    maxCount: 1,
    condition: (state) => working(state) && state.salary < 1800,
    params: (state) => ({ gain: between(state, 300, 400) }),
    choices: [
      { id: 'livret', effects: (_, p) => ({ livret: p.gain, wellbeing: 1 }) },
      { id: 'compte', effects: (_, p) => ({ cash: p.gain }) },
    ],
  }),
  simpleEvent({
    id: 'portefeuille_vole',
    kind: 'imprevu',
    stages: ALL_STAGES,
    bad: true,
    shock: 7,
    params: (state) => ({
      stolen: byStage(state, [20, 50], [30, 80]),
      card: between(state, 10, 20),
      stamp: 25,
    }),
    choices: [
      { id: 'tout', effects: (_, p) => ({ cash: -(p.stolen + p.card + p.stamp), energy: -2 }) },
      { id: 'carte', effects: (_, p) => ({ cash: -(p.stolen + p.card), wellbeing: -2 }) },
    ],
  }),
  simpleEvent({
    id: 'amende_oubliee_majoree',
    kind: 'imprevu',
    stages: ALL_STAGES,
    bad: true,
    shock: 6,
    params: (state) => {
      // Un stationnement, ou un petit excès de vitesse pour qui conduit.
      const speeding = state.hasCar && roll(state, 0, 1) === 1;
      return speeding ? { base: 135, total: 375 } : { base: 35, total: 75 };
    },
    choices: [
      { id: 'payer', effects: (_, p) => ({ cash: -p.total }) },
      { id: 'livret', effects: (_, p) => ({ livret: -p.total }) },
    ],
  }),

  // La santé
  simpleEvent({
    id: 'dents_de_sagesse',
    kind: 'imprevu',
    stages: ALL_STAGES,
    bad: true,
    shock: 6,
    page: 'fonds_urgence',
    // Tant qu'elles ne sont pas enlevées, elles peuvent revenir faire mal.
    condition: (state) => age(state) <= 25 && !state.flags.dents_sagesse_enlevees,
    // En clinique : la formule de base paie la part de la Sécurité sociale, pas les dépassements.
    params: (state) => ({ cost: careCost(state, { aucune: 380, base: 180, renforcee: 40 }) }),
    choices: [
      { id: 'livret', effects: (_, p) => ({ livret: -p.cost, energy: -3, flag: 'dents_sagesse_enlevees' }) },
      { id: 'compte', effects: (_, p) => ({ cash: -p.cost, energy: -3, flag: 'dents_sagesse_enlevees' }) },
      { id: 'attendre', effects: { wellbeing: -3, energy: -2 } },
    ],
  }),
  simpleEvent({
    id: 'couronne_dentaire',
    kind: 'imprevu',
    stages: ADULT,
    bad: true,
    shock: 6,
    maxCount: 2,
    page: 'fonds_urgence',
    // La couronne en métal est dans le « 100 % santé » ; la céramique sur une molaire, non.
    params: (state) => ({ cost: careCost(state, { aucune: 470, base: 280, renforcee: 60 }) }),
    choices: [
      { id: 'metal', effects: { wellbeing: -2 } },
      { id: 'ceramique', effects: (_, p) => ({ cash: -p.cost }) },
      { id: 'livret', effects: (_, p) => ({ livret: -p.cost }) },
    ],
  }),
  simpleEvent({
    id: 'jours_de_carence',
    kind: 'imprevu',
    stages: ADULT,
    bad: true,
    shock: 6,
    condition: (state) => working(state),
    // Trois jours de salaire sur trente.
    params: (state) => ({ loss: Math.round(state.salary * 0.1) }),
    choices: [
      { id: 'arret', effects: (_, p) => ({ cash: -p.loss, restDays: 10, wellbeing: 1 }) },
      { id: 'travailler', effects: { energy: -5, fatigueDays: 30, wellbeing: -2 } },
    ],
  }),
  simpleEvent({
    id: 'lunettes_opticien',
    kind: 'quotidien',
    pleasureCategory: 'soins',
    stages: ALL_STAGES,
    params: (state) => ({
      // Le panier « 100 % santé » : sans mutuelle, il reste la part que la Sécurité sociale ne paie pas.
      sante: careCost(state, { aucune: 50, base: 0, renforcee: 0 }),
      cost: careCost(state, { aucune: 340, base: 200, renforcee: 30 }),
      online: between(state, 180, 260),
    }),
    choices: [
      { id: 'sante', effects: (_, p) => ({ cash: -p.sante }) },
      { id: 'monture', effects: (_, p) => ({ cash: -p.cost, pleasure: 3 }) },
      { id: 'ligne', effects: (_, p) => ({ cash: -p.online, energy: -2, pleasure: 1 }) },
      { id: 'garder', effects: { energy: -1, wellbeing: -1 } },
    ],
  }),
  simpleEvent({
    id: 'medecin_depassement',
    kind: 'quotidien',
    stages: ALL_STAGES,
    // Une consultation à 50 € au lieu de 30 € : la formule de base ne paie pas le dépassement, la renforcée si.
    params: (state) => ({ extra: careCost(state, { aucune: 31, base: 22, renforcee: 2 }) }),
    choices: [
      { id: 'consulter', effects: (_, p) => ({ cash: -p.extra }) },
      { id: 'attendre', effects: { energy: -3, wellbeing: -1 } },
    ],
  }),
  simpleEvent({
    id: 'osteopathe_dos',
    kind: 'quotidien',
    stages: ADULT,
    params: (state) => {
      const cost = between(state, 210, 250);
      // Le forfait « médecines douces » de la mutuelle : modeste en formule de base, large avec la renforcée.
      return { cost, gain: Math.max(0, cost - careCost(state, { aucune: 75, base: 40, renforcee: 15 })) };
    },
    choices: [
      {
        id: 'osteo',
        effects: (_, p) => ({
          cash: -p.cost,
          wellbeing: 2,
          ...(p.gain > 0 ? { followUp: { event: 'remboursement_osteopathe', days: 30 } } : {}),
        }),
      },
      { id: 'medecin', effects: { energy: -3 } },
      { id: 'attendre', effects: { wellbeing: -2 } },
    ],
  }),
  simpleEvent({
    // Un mois après la séance chez l'ostéopathe.
    id: 'remboursement_osteopathe',
    kind: 'chance',
    pleasureCategory: 'loisirs',
    stages: ADULT,
    manual: true,
    // Le remboursement {gain} vient de la séance, selon la formule de la mutuelle à ce moment-là.
    choices: [
      { id: 'livret', effects: (_, p) => ({ livret: p.gain, wellbeing: 1 }) },
      { id: 'cinema', effects: { pleasure: 2 } },
    ],
  }),

  // Assurances et épargne
  simpleEvent({
    id: 'assurance_telephone',
    kind: 'tentation',
    stages: ALL_STAGES,
    condition: (state) => !hasSubscription(state, 'assurance_telephone'),
    params: (state) => ({ monthly: between(state, 8, 12), franchise: 50 }),
    choices: [
      {
        id: 'prendre',
        effects: (_, p) => ({ subscribe: { id: 'assurance_telephone', amount: p.monthly }, wellbeing: 1 }),
      },
      { id: 'refuser' },
    ],
  }),
  simpleEvent({
    id: 'assurance_vie_frais',
    kind: 'quotidien',
    stages: ADULT,
    maxCount: 1,
    // Le conseiller propose d'ouvrir l'assurance-vie de la banque (voir game/av.ts) : une seule à la fois.
    condition: (state) => state.livret >= 2000 && canOpenAv(state),
    params: (state) => {
      const amount = Math.max(2000, Math.floor(Math.min(state.livret, 5000) / 500) * 500);
      return { amount, fees: Math.round(amount * 0.03), reduced: Math.round(amount * 0.01) };
    },
    choices: [
      // L'argent passe du livret à l'assurance-vie : seuls les frais d'entrée sont perdus.
      {
        id: 'signer',
        effects: (_, p) => ({ bankLifeInsurance: { amount: p.amount, entryFee: 0.03 } }),
      },
      { id: 'reflechir', effects: { followUp: { event: 'assurance_vie_relance', days: 7 } } },
      { id: 'refuser' },
    ],
  }),
  simpleEvent({
    // Une semaine après avoir demandé à réfléchir : les frais ont baissé.
    id: 'assurance_vie_relance',
    kind: 'quotidien',
    stages: ADULT,
    manual: true,
    // Si un contrat a été ouvert entre-temps, le conseiller ne rappelle pas.
    condition: canOpenAv,
    choices: [
      {
        id: 'signer',
        effects: (_, p) => ({ bankLifeInsurance: { amount: p.amount, entryFee: 0.01 } }),
      },
      { id: 'decliner' },
    ],
  }),
  simpleEvent({
    id: 'livret_populaire',
    kind: 'quotidien',
    stages: ADULT,
    maxCount: 1,
    condition: (state) => working(state) && state.salary <= 1900 && state.livret >= 1000,
    // Environ un point d'intérêt de plus sur un an, dans la limite de 10 000 €.
    params: (state) => ({ bonus: Math.max(10, Math.round(Math.min(state.livret, 10000) * 0.01)) }),
    choices: [
      { id: 'ouvrir', effects: (_, p) => ({ livret: p.bonus, energy: -2 }) },
      { id: 'rester' },
    ],
  }),

  // Le budget au quotidien
  simpleEvent({
    id: 'prix_qui_montent',
    kind: 'quotidien',
    stages: ALL_STAGES,
    page: 'inflation',
    params: (state) => ({ more: between(state, 6, 12) }),
    choices: [
      { id: 'habitudes', effects: (_, p) => ({ addExpense: { id: 'courses', amount: p.more } }) },
      { id: 'comparer', effects: { energy: -3, wellbeing: -2 } },
    ],
  }),
  simpleEvent({
    id: 'appli_budget',
    kind: 'quotidien',
    stages: ALL_STAGES,
    page: 'budget',
    condition: (state) => !hasSubscription(state, 'appli_budget'),
    params: (state) => ({ premium: between(state, 3, 5) }),
    choices: [
      { id: 'gratuite', effects: { energy: -2, xp: { skill: 'organisation', amount: 10 } } },
      {
        id: 'complete',
        effects: (_, p) => ({
          subscribe: { id: 'appli_budget', amount: p.premium },
          xp: { skill: 'organisation', amount: 10 },
          wellbeing: 1,
        }),
      },
      { id: 'ignorer' },
    ],
  }),

  // Les petites surprises
  simpleEvent({
    id: 'billet_par_terre',
    kind: 'chance',
    pleasureCategory: 'repas',
    stages: ALL_STAGES,
    params: (state) => ({ gain: roll(state, 0, 1) === 1 ? 50 : 20 }),
    choices: [
      { id: 'ranger', effects: (_, p) => ({ livret: p.gain, wellbeing: 1 }) },
      { id: 'dejeuner', effects: { pleasure: 2 } },
      { id: 'deposer', effects: { energy: -1, wellbeing: 2 } },
    ],
  }),
  simpleEvent({
    id: 'erreur_de_caisse',
    kind: 'chance',
    stages: ALL_STAGES,
    params: (state) => ({ gain: between(state, 20, 40) }),
    choices: [
      { id: 'rendre', effects: { energy: -1, wellbeing: 2 } },
      { id: 'garder', effects: (_, p) => ({ cash: p.gain }) },
    ],
  }),
  simpleEvent({
    id: 'trop_percu_operateur',
    kind: 'chance',
    stages: ADULT,
    params: (state) => ({ gain: between(state, 30, 90) }),
    choices: [
      { id: 'livret', effects: (_, p) => ({ livret: p.gain, wellbeing: 1 }) },
      { id: 'compte', effects: (_, p) => ({ cash: p.gain }) },
    ],
  }),
  simpleEvent({
    id: 'compte_oublie',
    kind: 'chance',
    stages: ALL_STAGES,
    maxCount: 1,
    page: 'interet_compose',
    params: (state) => {
      const deposit = between(state, 100, 250);
      // Une vingtaine d'années d'intérêts à 2 % par an : environ la moitié en plus.
      return { deposit, gain: Math.round((deposit * 1.5) / 5) * 5 };
    },
    choices: [
      { id: 'livret', effects: (_, p) => ({ livret: p.gain, wellbeing: 2 }) },
      { id: 'compte', effects: (_, p) => ({ cash: p.gain }) },
    ],
  }),
];
